import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Loader2, Plus, Save, Trash2, X, KeyRound, Eye, EyeOff } from 'lucide-react';
import { client, useAuth } from './AuthContext';

export default function AdminUsers() {
  const { admin: me } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [resetFor, setResetFor] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await client.get('/api/admin/users');
      setUsers(data);
    } catch (err) {
      if (err?.response?.status === 403) {
        toast.error('Hanya super admin yang bisa mengelola user');
      } else {
        toast.error('Gagal memuat user');
      }
    }
    setLoading(false);
  };

  useEffect(() => {
    load();
  }, []);

  const changeRole = async (u, newRole) => {
    try {
      await client.put(`/api/admin/users/${u.id}`, { role: newRole });
      toast.success('Role diperbarui');
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Gagal mengubah role');
    }
  };

  const remove = async (u) => {
    if (!window.confirm(`Hapus user ${u.email}?`)) return;
    try {
      await client.delete(`/api/admin/users/${u.id}`);
      toast.success('User dihapus');
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Gagal menghapus');
    }
  };

  return (
    <div data-testid="admin-users-page">
      <div className="flex items-start justify-between mb-8">
        <div>
          <p className="text-xs tracking-[0.25em] text-px-red font-semibold mb-3">
            KELOLA USER
          </p>
          <h1 className="font-display text-4xl font-bold text-neutral-900 mb-2">
            Kelola User Admin
          </h1>
          <p className="text-neutral-600">
            Tambahkan admin lain, atur role, atau reset password mereka.
          </p>
        </div>
        <button
          onClick={() => setCreating(true)}
          data-testid="add-user-btn"
          className="inline-flex items-center gap-2 bg-neutral-900 hover:bg-px-red text-white text-sm font-semibold px-5 py-2.5 rounded-full transition-colors"
        >
          <Plus className="w-4 h-4" /> Tambah User
        </button>
      </div>

      <div className="bg-white rounded-2xl border border-neutral-200 overflow-hidden">
        {loading ? (
          <p className="p-8 text-sm text-neutral-500">Memuat...</p>
        ) : users.length === 0 ? (
          <p className="p-8 text-sm text-neutral-500">Belum ada user.</p>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-neutral-50 border-b border-neutral-200 text-left text-xs uppercase text-neutral-600">
              <tr>
                <th className="px-4 py-3">Email / Username</th>
                <th className="px-4 py-3">Nama</th>
                <th className="px-4 py-3">Role</th>
                <th className="px-4 py-3 text-right">Aksi</th>
              </tr>
            </thead>
            <tbody>
              {users.map((u) => {
                const isMe = u.id === me?.id;
                return (
                  <tr
                    key={u.id}
                    data-testid={`user-row-${u.id}`}
                    className="border-b border-neutral-100 last:border-0"
                  >
                    <td className="px-4 py-3">
                      <span className="font-semibold text-neutral-900">
                        {u.email}
                      </span>
                      {isMe && (
                        <span className="ml-2 text-[10px] font-bold text-px-red">
                          (ANDA)
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-neutral-700">{u.name}</td>
                    <td className="px-4 py-3">
                      <select
                        value={u.role}
                        onChange={(e) => changeRole(u, e.target.value)}
                        className="text-xs bg-neutral-100 border border-neutral-200 rounded-full px-3 py-1"
                        data-testid={`role-select-${u.id}`}
                      >
                        <option value="admin">Admin</option>
                        <option value="super_admin">Super Admin</option>
                      </select>
                    </td>
                    <td className="px-4 py-3 text-right space-x-1">
                      <button
                        onClick={() => setResetFor(u)}
                        className="inline-flex items-center gap-1 text-xs text-neutral-600 hover:text-neutral-900 px-2 py-1.5"
                        title="Reset password"
                      >
                        <KeyRound className="w-3.5 h-3.5" /> Reset password
                      </button>
                      {!isMe && (
                        <button
                          onClick={() => remove(u)}
                          className="p-1.5 text-neutral-500 hover:text-red-600"
                          title="Hapus"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {creating && (
        <CreateUserModal
          onClose={() => setCreating(false)}
          onSaved={() => {
            setCreating(false);
            load();
          }}
        />
      )}
      {resetFor && (
        <ResetPasswordModal
          user={resetFor}
          onClose={() => setResetFor(null)}
          onSaved={() => setResetFor(null)}
        />
      )}
    </div>
  );
}

function CreateUserModal({ onClose, onSaved }) {
  const [form, setForm] = useState({
    email: '',
    name: '',
    password: '',
    role: 'admin',
  });
  const [showPw, setShowPw] = useState(false);
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await client.post('/api/admin/users', {
        ...form,
        email: form.email.trim().toLowerCase(),
      });
      toast.success('User dibuat');
      onSaved();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Gagal membuat user');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal title="Tambah User Admin" onClose={onClose}>
      <form onSubmit={submit} className="space-y-4">
        <Field label="Email / Username" required>
          <input
            required
            className={ic}
            value={form.email}
            onChange={(e) => setForm({ ...form, email: e.target.value })}
            data-testid="new-user-email"
          />
        </Field>
        <Field label="Nama Tampilan">
          <input
            className={ic}
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
          />
        </Field>
        <Field label="Password Awal" required>
          <div className="relative">
            <input
              required
              type={showPw ? 'text' : 'password'}
              className={`${ic} pr-10`}
              value={form.password}
              onChange={(e) => setForm({ ...form, password: e.target.value })}
              data-testid="new-user-password"
            />
            <button
              type="button"
              onClick={() => setShowPw((v) => !v)}
              data-testid="new-user-password-toggle"
              aria-label={showPw ? 'Sembunyikan password' : 'Tampilkan password'}
              className="absolute inset-y-0 right-2 flex items-center text-neutral-500 hover:text-neutral-900"
            >
              {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
            </button>
          </div>
          <p className="text-xs text-neutral-500 mt-1">
            User bisa mengganti sendiri lewat fitur reset nanti.
          </p>
        </Field>
        <Field label="Role">
          <select
            className={ic}
            value={form.role}
            onChange={(e) => setForm({ ...form, role: e.target.value })}
            data-testid="new-user-role"
          >
            <option value="admin">Admin (kelola konten & katalog)</option>
            <option value="super_admin">
              Super Admin (juga kelola user)
            </option>
          </select>
        </Field>
        <div className="flex justify-end gap-2 pt-2">
          <button
            type="button"
            onClick={onClose}
            className="border border-neutral-300 rounded-full text-sm font-semibold px-5 py-2"
          >
            Batal
          </button>
          <button
            type="submit"
            disabled={saving}
            data-testid="save-user-btn"
            className="inline-flex items-center gap-2 bg-px-red hover:bg-red-700 disabled:opacity-60 text-white font-semibold px-6 py-2 rounded-full"
          >
            {saving ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Save className="w-4 h-4" />
            )}
            Buat User
          </button>
        </div>
      </form>
    </Modal>
  );
}

function ResetPasswordModal({ user, onClose, onSaved }) {
  const [pw, setPw] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!pw.trim()) return;
    setSaving(true);
    try {
      await client.put(`/api/admin/users/${user.id}`, { password: pw });
      toast.success('Password diperbarui');
      onSaved();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Gagal reset password');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal title={`Reset password: ${user.email}`} onClose={onClose}>
      <form onSubmit={submit} className="space-y-4">
        <Field label="Password Baru" required>
          <div className="relative">
            <input
              required
              type={showPw ? 'text' : 'password'}
              className={`${ic} pr-10`}
              value={pw}
              onChange={(e) => setPw(e.target.value)}
              autoFocus
              data-testid="reset-password-input"
            />
            <button
              type="button"
              onClick={() => setShowPw((v) => !v)}
              data-testid="reset-password-toggle"
              aria-label={showPw ? 'Sembunyikan password' : 'Tampilkan password'}
              className="absolute inset-y-0 right-2 flex items-center text-neutral-500 hover:text-neutral-900"
            >
              {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
            </button>
          </div>
        </Field>
        <div className="flex justify-end gap-2 pt-2">
          <button
            type="button"
            onClick={onClose}
            className="border border-neutral-300 rounded-full text-sm font-semibold px-5 py-2"
          >
            Batal
          </button>
          <button
            type="submit"
            disabled={saving}
            className="inline-flex items-center gap-2 bg-px-red hover:bg-red-700 disabled:opacity-60 text-white font-semibold px-6 py-2 rounded-full"
          >
            {saving ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <KeyRound className="w-4 h-4" />
            )}
            Simpan Password
          </button>
        </div>
      </form>
    </Modal>
  );
}

const ic =
  'mt-1 w-full bg-neutral-50 border border-neutral-200 focus:border-neutral-900 outline-none rounded-lg px-3 py-2 text-sm';

const Field = ({ label, children, required }) => (
  <div>
    <label className="text-[11px] uppercase tracking-wider text-neutral-600 font-semibold">
      {label}
      {required && ' *'}
    </label>
    {children}
  </div>
);

function Modal({ title, onClose, children }) {
  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl w-full max-w-md overflow-hidden">
        <div className="flex items-center justify-between border-b border-neutral-200 px-6 py-4">
          <h2 className="font-display text-lg font-bold">{title}</h2>
          <button
            onClick={onClose}
            className="text-neutral-500 hover:text-neutral-900"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="px-6 py-5">{children}</div>
      </div>
    </div>
  );
}
